// src/components/templates/diary/DiaryResourcesView.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';

import {
  FileText, Video, Car, Home, TrendingUp, Landmark, Package, Gem, Book,
  Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye, ExternalLink,
  Upload, Link as LinkIcon, Youtube, File, Image, FileSpreadsheet,
  X, Save, FolderOpen, Grid, List, Download, Copy, Check, Loader,
  Wrench, Globe, Code, Play, ChevronDown, Calendar, DollarSign,
  Tag, MapPin, Hash, AlertCircle, Briefcase, ArrowLeft, Server
} from 'lucide-react';
import { Button, Card, Badge, Input, TextArea, Select, Modal } from './UIComponents';
import { auth, db } from '../../config/firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';


// --- CONSTANTS ---
const ResourceTabs = {
  MEDIA: 'MEDIA',
  ASSETS: 'ASSETS',
  TOOLS: 'TOOLS'
};

// Tools view states
// Tools view states
const ToolsViewState = {
  LIST: 'LIST',          
  INTERNAL: 'INTERNAL',  
  EXTERNAL: 'EXTERNAL',  
  VIEWER: 'VIEWER',      
  EDITOR: 'EDITOR'       
};

const MediaType = {
  DOCUMENT: 'DOCUMENT',
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE',
  SPREADSHEET: 'SPREADSHEET',
  OTHER: 'OTHER'
};

const AssetCategory = {
  VEHICLE: 'VEHICLE',
  PROPERTY: 'PROPERTY',
  SHARES: 'SHARES',
  INVESTMENT: 'INVESTMENT',
  INSURANCE: 'INSURANCE',
  COLLECTIBLE: 'COLLECTIBLE',
  EQUIPMENT: 'EQUIPMENT',
  OTHER: 'OTHER'
};

const AssetCategoryConfig = {
  [AssetCategory.VEHICLE]: { icon: Car, label: 'Vehicles', color: 'blue' },
  [AssetCategory.PROPERTY]: { icon: Home, label: 'Properties', color: 'green' },
  [AssetCategory.SHARES]: { icon: TrendingUp, label: 'Shares & Stocks', color: 'purple' },
  [AssetCategory.INVESTMENT]: { icon: Landmark, label: 'Investments', color: 'amber' },
  [AssetCategory.INSURANCE]: { icon: Briefcase, label: 'Insurance', color: 'cyan' },
  [AssetCategory.COLLECTIBLE]: { icon: Gem, label: 'Collectibles', color: 'pink' },
  [AssetCategory.EQUIPMENT]: { icon: Package, label: 'Equipment', color: 'orange' },
  [AssetCategory.OTHER]: { icon: FolderOpen, label: 'Other Assets', color: 'gray' }
};

const ToolType = {
  INTERNAL: 'INTERNAL',  // Owned tools (embeds, HTML widgets)
  EXTERNAL: 'EXTERNAL'   // External links
};

// --- HELPER FUNCTIONS ---
const getYoutubeId = (url) => {
  const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

const getYoutubeThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
};

const formatCurrency = (amount, currency = 'ZAR') => {
  if (!amount) return '-';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA', { 
    year: 'numeric', month: 'short', day: 'numeric' 
  });
};

const getFileIcon = (type, mimeType) => {
  if (type === MediaType.VIDEO) return Video;
  if (type === MediaType.IMAGE) return Image;
  if (type === MediaType.SPREADSHEET) return FileSpreadsheet;
  if (mimeType?.includes('pdf')) return FileText;
  return File;
};

const TabButton = ({ active, onClick, icon: Icon, label, count, customStyling }) => (
  <button
    onClick={onClick}
    style={{ color: active ? customStyling.primaryColor : undefined }}
    className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition-all ${
      active
        ? 'border-indigo-600 bg-indigo-50/50'
        : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count > 0 && (
      <span className={`px-2 py-0.5 rounded-full text-xs ${
        active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const DropdownMenu = ({ children, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="relative">
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 py-1">
            {React.Children.map(children, child =>
              React.cloneElement(child, { onClick: () => { child.props.onClick?.(); setIsOpen(false); } })
            )}
          </div>
        </>
      )}
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${
      danger ? 'text-red-600' : 'text-gray-700'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

// --- MEDIA TAB COMPONENTS ---
const MediaCard = ({ item, onEdit, onDelete, onView, viewMode }) => {
  const isYoutube = item.type === MediaType.VIDEO && item.youtubeUrl;
  const thumbnail = isYoutube ? getYoutubeThumbnail(item.youtubeUrl) : item.thumbnailUrl;
  const FileIcon = getFileIcon(item.type, item.mimeType);

  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all">
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {thumbnail ? (
            <img src={thumbnail} alt="" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <FileIcon className="w-6 h-6 text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
          <p className="text-sm text-gray-500 truncate">{item.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{formatDate(item.createdAt)}</span>
          <DropdownMenu trigger={
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }>
            <MenuItem icon={Eye} label="View" onClick={() => onView(item)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(item)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(item.id)} danger />
          </DropdownMenu>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all group">
      <div className="aspect-video bg-gray-100 relative">
        {thumbnail ? (
          <img src={thumbnail} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileIcon className="w-12 h-12 text-gray-300" />
          </div>
        )}
        {isYoutube && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center">
              <Play className="w-6 h-6 text-white ml-1" />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu trigger={
            <button className="p-2 bg-white/90 hover:bg-white rounded-lg shadow-sm">
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
          }>
            <MenuItem icon={Eye} label="View" onClick={() => onView(item)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(item)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(item.id)} danger />
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4">
        <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
        <p className="text-sm text-gray-500 truncate mt-1">{item.description || 'No description'}</p>
        <div className="flex items-center gap-2 mt-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.type === MediaType.VIDEO ? 'bg-red-100 text-red-700' :
            item.type === MediaType.IMAGE ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {item.type}
          </span>
          <span className="text-xs text-gray-400">{formatDate(item.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

// UPDATED SECTION FOR MediaFormModal IN DiaryResourcesView.jsx

const MediaFormModal = ({ isOpen, onClose, onSave, mediaCategories, item, bookId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: MediaType.DOCUMENT,
    youtubeUrl: '', 
    fileUrl: '',
    thumbnailUrl: '',
    tags: [],
    category: '',
    ...item
  });
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !bookId) return;

    setUploading(true);
    setUploadError('');

    try {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Determine file type and path
      let fileType = 'documents';
      let mediaType = MediaType.DOCUMENT;
      
      if (file.type.startsWith('image/')) {
        fileType = 'images';
        mediaType = MediaType.IMAGE;
      } else if (file.type.includes('sheet') || file.type.includes('excel')) {
        fileType = 'spreadsheets';
        mediaType = MediaType.SPREADSHEET;
      } else if (file.type === 'application/pdf') {
        fileType = 'documents';
        mediaType = MediaType.DOCUMENT;
      }

      // Upload file using firestoreService
      const storagePath = `pb-${bookId}/diary-media/${fileType}`;
      const { firestoreService } = await import('../../services/firestoreService');
      const result = await firestoreService.uploadFile(file, storagePath);

      // Update form data with uploaded file URL
      setFormData(prev => ({
        ...prev,
        fileUrl: result.url,
        type: mediaType,
        name: prev.name || file.name
      }));

      console.log('File uploaded successfully:', result);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    
    // For videos, require either YouTube URL or file URL
    if (formData.type === MediaType.VIDEO && !formData.youtubeUrl && !formData.fileUrl) {
      setUploadError('Please provide a YouTube URL or upload a video file');
      return;
    }

    // For documents/images, require file URL
    if ((formData.type === MediaType.DOCUMENT || formData.type === MediaType.IMAGE || formData.type === MediaType.SPREADSHEET) && !formData.fileUrl) {
      setUploadError('Please upload a file');
      return;
    }

    onSave({
      ...formData,
      id: item?.id || Date.now().toString(),
      createdAt: item?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Edit Media' : 'Add Media'} size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Document or video name"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Type</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={MediaType.DOCUMENT}>Document</option>
            <option value={MediaType.VIDEO}>Video</option>
            <option value={MediaType.IMAGE}>Image</option>
            <option value={MediaType.SPREADSHEET}>Spreadsheet</option>
            <option value={MediaType.OTHER}>Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Category</label>
          <div className="flex gap-2">
            <select
              value={formData.category || ''}
              onChange={(e) => handleChange('category', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select category...</option>
              {mediaCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* File Upload Section */}
        {(formData.type === MediaType.DOCUMENT || formData.type === MediaType.IMAGE || formData.type === MediaType.SPREADSHEET) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Upload File *
            </label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept={
                formData.type === MediaType.IMAGE ? 'image/*' :
                formData.type === MediaType.SPREADSHEET ? '.xlsx,.xls,.csv' :
                '.pdf,.doc,.docx,.txt'
              }
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-indigo-600"
            >
              {uploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : formData.fileUrl ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">File uploaded</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Choose file</span>
                </>
              )}
            </button>
            {uploadError && (
              <p className="text-xs text-red-600 mt-1">{uploadError}</p>
            )}
          </div>
        )}

        {/* YouTube URL for Videos */}
        {formData.type === MediaType.VIDEO && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              YouTube URL
            </label>
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => handleChange('youtubeUrl', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Tags</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              placeholder="Add tag..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Button variant="secondary" onClick={handleAddTag}>Add</Button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.name.trim() || uploading}>
          <Save className="w-4 h-4" /> {item ? 'Update' : 'Add Media'}
        </Button>
      </div>
    </Modal>
  );
};

const MediaViewModal = ({ isOpen, onClose, item }) => {
  if (!item) return null;
  const isYoutube = item.type === MediaType.VIDEO && item.youtubeUrl;
  const youtubeId = getYoutubeId(item.youtubeUrl);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={item.name} size={isYoutube ? 'lg' : 'md'}>
      {isYoutube && youtubeId && (
        <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={item.name}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}
      
      {!isYoutube && item.fileUrl && (
        <div className="mb-4">
          <a 
            href={item.fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <ExternalLink className="w-4 h-4" />
            Open File
          </a>
        </div>
      )}

      {item.description && (
        <p className="text-gray-600 mb-4">{item.description}</p>
      )}

      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.tags.map(tag => (
            <span key={tag} className="px-2 py-1 bg-gray-100 rounded-full text-sm text-gray-600">
              {tag}
            </span>
          ))}
        </div>
      )}
    </Modal>
  );
};

// --- ASSET REGISTER COMPONENTS ---
const AssetCard = ({ asset, onEdit, onDelete, onView }) => {
  const config = AssetCategoryConfig[asset.category] || AssetCategoryConfig[AssetCategory.OTHER];
  const Icon = config.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all">
      <div className={`h-2 bg-${config.color}-500`} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg bg-${config.color}-100 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${config.color}-600`} />
          </div>
          <DropdownMenu trigger={
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }>
            <MenuItem icon={Eye} label="View Details" onClick={() => onView(asset)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(asset)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(asset.id)} danger />
          </DropdownMenu>
        </div>

        <h4 className="font-semibold text-gray-900 mb-1">{asset.name}</h4>
        <p className="text-sm text-gray-500 mb-3">{config.label}</p>

        {asset.value && (
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-green-600">{formatCurrency(asset.value, asset.currency)}</span>
          </div>
        )}

        <div className="space-y-2 text-sm">
          {asset.identifier && (
            <div className="flex items-center gap-2 text-gray-500">
              <Hash className="w-4 h-4" />
              <span className="truncate">{asset.identifier}</span>
            </div>
          )}
          {asset.location && (
            <div className="flex items-center gap-2 text-gray-500">
              <MapPin className="w-4 h-4" />
              <span className="truncate">{asset.location}</span>
            </div>
          )}
          {asset.purchaseDate && (
            <div className="flex items-center gap-2 text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>Acquired {formatDate(asset.purchaseDate)}</span>
            </div>
          )}
        </div>

        {asset.documents?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">{asset.documents.length} document(s) attached</span>
          </div>
        )}
      </div>
    </div>
  );
};

const AssetFormModal = ({ isOpen, onClose, onSave, asset }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: AssetCategory.OTHER,
    description: '',
    value: '',
    currency: 'ZAR',
    purchaseDate: '',
    purchasePrice: '',
    identifier: '',
    location: '',
    notes: '',
    documents: [],
    links: [],
    ...asset
  });
  const [newLink, setNewLink] = useState({ name: '', url: '' });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLink = () => {
    if (newLink.name && newLink.url) {
      setFormData(prev => ({
        ...prev,
        links: [...(prev.links || []), { ...newLink, id: Date.now().toString() }]
      }));
      setNewLink({ name: '', url: '' });
    }
  };

  const handleRemoveLink = (linkId) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter(l => l.id !== linkId)
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onSave({
      ...formData,
      value: formData.value ? parseFloat(formData.value) : null,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
      id: asset?.id || Date.now().toString(),
      createdAt: asset?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={asset ? 'Edit Asset' : 'Add Asset'} size="lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Asset Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., 2020 Toyota Corolla, House at 123 Main St"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Category</label>
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {Object.entries(AssetCategoryConfig).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Identifier</label>
          <input
            type="text"
            value={formData.identifier}
            onChange={(e) => handleChange('identifier', e.target.value)}
            placeholder="VIN, Title deed number, Stock symbol..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Current Value</label>
          <div className="flex gap-2">
            <select
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ZAR">ZAR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
            <input
              type="number"
              value={formData.value}
              onChange={(e) => handleChange('value', e.target.value)}
              placeholder="0.00"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Purchase Price</label>
          <input
            type="number"
            value={formData.purchasePrice}
            onChange={(e) => handleChange('purchasePrice', e.target.value)}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Purchase Date</label>
          <input
            type="date"
            value={formData.purchaseDate}
            onChange={(e) => handleChange('purchaseDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="Address or location"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Details about this asset..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Related Links</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newLink.name}
              onChange={(e) => setNewLink(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Link name"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="url"
              value={newLink.url}
              onChange={(e) => setNewLink(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Button variant="secondary" onClick={handleAddLink}>Add</Button>
          </div>
          {formData.links?.length > 0 && (
            <div className="space-y-2">
              {formData.links.map(link => (
                <div key={link.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{link.name}</span>
                    <span className="text-xs text-gray-400 truncate max-w-xs">{link.url}</span>
                  </div>
                  <button onClick={() => handleRemoveLink(link.id)} className="text-gray-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.name.trim()}>
          <Save className="w-4 h-4" /> {asset ? 'Update' : 'Add Asset'}
        </Button>
      </div>
    </Modal>
  );
};

const AssetViewModal = ({ isOpen, onClose, asset }) => {
  if (!asset) return null;
  const config = AssetCategoryConfig[asset.category] || AssetCategoryConfig[AssetCategory.OTHER];
  const Icon = config.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={asset.name} size="lg">
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl bg-${config.color}-100 flex items-center justify-center`}>
            <Icon className={`w-7 h-7 text-${config.color}-600`} />
          </div>
          <div>
            <p className="text-sm text-gray-500">{config.label}</p>
            {asset.value && (
              <p className="text-2xl font-bold text-green-600">{formatCurrency(asset.value, asset.currency)}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {asset.identifier && (
            <div>
              <p className="text-xs text-gray-400 uppercase">Identifier</p>
              <p className="font-medium">{asset.identifier}</p>
            </div>
          )}
          {asset.location && (
            <div>
              <p className="text-xs text-gray-400 uppercase">Location</p>
              <p className="font-medium">{asset.location}</p>
            </div>
          )}
          {asset.purchaseDate && (
            <div>
              <p className="text-xs text-gray-400 uppercase">Purchase Date</p>
              <p className="font-medium">{formatDate(asset.purchaseDate)}</p>
            </div>
          )}
          {asset.purchasePrice && (
            <div>
              <p className="text-xs text-gray-400 uppercase">Purchase Price</p>
              <p className="font-medium">{formatCurrency(asset.purchasePrice, asset.currency)}</p>
            </div>
          )}
        </div>

        {asset.description && (
          <div>
            <p className="text-xs text-gray-400 uppercase mb-1">Description</p>
            <p className="text-gray-700">{asset.description}</p>
          </div>
        )}

        {asset.links?.length > 0 && (
          <div>
            <p className="text-xs text-gray-400 uppercase mb-2">Related Links</p>
            <div className="space-y-2">
              {asset.links.map(link => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 text-indigo-600" />
                  <span className="font-medium text-indigo-600">{link.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {asset.notes && (
          <div>
            <p className="text-xs text-gray-400 uppercase mb-1">Notes</p>
            <p className="text-gray-600 text-sm">{asset.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

// --- TOOLS OVERVIEW COMPONENT ---
// --- TOOLS OVERVIEW COMPONENT ---
const ToolsOverview = ({ tools = [], onNavigate, onAdd, onAddBooks }) => {
  const internalTools = tools.filter(t => t.type === ToolType.INTERNAL);
  const externalTools = tools.filter(t => t.type === ToolType.EXTERNAL);
  const bookTools = internalTools.filter(t => t.sourceType === 'book');

  const categories = [
    {
      key: ToolsViewState.INTERNAL,
      icon: Server,
      title: 'Internal Tools',
      description: 'Owned digital tools, embeds, and custom widgets',
      count: internalTools.length,
      color: 'indigo',
      gradient: 'from-indigo-500 to-purple-600'
    },
    {
      key: ToolsViewState.EXTERNAL,
      icon: Globe,
      title: 'External Tools',
      description: 'Web apps, services, and external resources',
      count: externalTools.length,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Digital Tools</h2>
        <p className="text-gray-600">Manage your internal tools and external resources</p>
      </div>

      {/* Published Books Section */}
      {bookTools.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Book className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Published Books</h3>
                <p className="text-sm text-gray-600">{bookTools.length} book{bookTools.length !== 1 ? 's' : ''} linked</p>
              </div>
            </div>
            <Button onClick={onAddBooks} variant="secondary" size="sm">
              <Plus className="w-4 h-4" /> Add Books
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bookTools.slice(0, 6).map(tool => (
              <div key={tool.id} className="bg-white rounded-lg p-3 border border-purple-100">
                <div className="flex items-center gap-2 mb-1">
                  <Book className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-sm text-gray-900 truncate">{tool.name}</span>
                </div>
                <p className="text-xs text-gray-600 truncate">{tool.description}</p>
              </div>
            ))}
          </div>
          
          {bookTools.length > 6 && (
            <button 
              onClick={() => onNavigate(ToolsViewState.INTERNAL)}
              className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View all {bookTools.length} books →
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.key}
              onClick={() => onNavigate(cat.key)}
              className="group relative bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-transparent hover:shadow-xl transition-all duration-300 text-left overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              
              <div className="relative z-10">
                <div className={`w-16 h-16 bg-${cat.color}-50 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mb-4 transition-colors`}>
                  <Icon className={`w-8 h-8 text-${cat.color}-600 group-hover:text-white transition-colors`} />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white mb-2 transition-colors">
                  {cat.title}
                </h3>
                
                <p className="text-gray-600 group-hover:text-white/90 mb-4 transition-colors">
                  {cat.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium text-${cat.color}-600 group-hover:text-white transition-colors`}>
                    {cat.count} {cat.count === 1 ? 'tool' : 'tools'}
                  </span>
                  <ExternalLink className={`w-5 h-5 text-${cat.color}-400 group-hover:text-white transition-colors`} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex justify-center gap-3 pt-6">
        <Button onClick={onAddBooks} variant="primary">
          <Book className="w-4 h-4" /> Add Published Books
        </Button>
        <Button onClick={() => onAdd(ToolType.INTERNAL)} variant="secondary">
          <Plus className="w-4 h-4" /> Add Custom Tool
        </Button>
        <Button onClick={() => onAdd(ToolType.EXTERNAL)} variant="secondary">
          <Plus className="w-4 h-4" /> Add External Link
        </Button>
      </div>
    </div>
  );
};

// --- TOOLS LIST COMPONENT ---
const ToolsList = ({ 
  tools = [], 
  type, 
  viewMode = 'grid',
  onEdit, 
  onDelete, 
  onOpen,
  onBack 
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = useMemo(() => {
    return tools.filter(t => {
      const matchesType = t.type === type;
      const matchesSearch = 
        t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [tools, type, searchQuery]);

  const isInternal = type === ToolType.INTERNAL;
  const title = isInternal ? 'Internal Tools' : 'External Tools';
  const Icon = isInternal ? Server : Globe;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Icon className="w-6 h-6 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search tools..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Content */}
      {filteredTools.length === 0 ? (
        <EmptyState
          icon={Icon}
          title={`No ${isInternal ? 'internal' : 'external'} tools yet`}
          description={isInternal 
            ? 'Add custom widgets, embeds, and owned digital tools'
            : 'Add links to your favorite web apps and services'
          }
          action={null}
        />
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
          : 'space-y-3'
        }>
          {filteredTools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onEdit={onEdit}
              onDelete={onDelete}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- TOOL EDITOR COMPONENT ---
const ToolEditor = ({ tool = null, type, onSave, onCancel }) => {
  const [formData, setFormData] = useState(tool || {
    type: type || 'INTERNAL',
    name: '',
    description: '',
    url: '',
    embedCode: '',
    htmlCode: '',
    icon: '',
    category: '',
    tags: [],
    sourceType: null,  // 'book' or null
    sourceBookId: null
  });

  const [tagInput, setTagInput] = useState('');
  const [contentMode, setContentMode] = useState(
    tool?.sourceType === 'book' ? 'book-url' :
    tool?.embedCode ? 'embed' : 
    tool?.htmlCode ? 'html' : 
    null
  );
  const [loadingBook, setLoadingBook] = useState(false);
  const [bookError, setBookError] = useState(null);

  const isInternal = formData.type === 'INTERNAL';

  // Extract book ID from URL
  // Extract book ID or custom domain info from URL
  const parseBookUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname;
      const pathname = urlObj.pathname;

      // Check if it's a standard book URL pattern: /book/abc123 or /books/abc123
      const bookIdMatch = pathname.match(/\/books?\/([a-zA-Z0-9_-]+)/);
      if (bookIdMatch) {
        return {
          type: 'standard',
          bookId: bookIdMatch[1],
          url
        };
      }

      // Otherwise, it might be a custom domain
      // Extract path (remove leading/trailing slashes)
      const path = pathname.replace(/^\/|\/$/g, '');

      return {
        type: 'custom',
        domain: hostname,
        path: path || '', // Empty string if root path
        url
      };
    } catch (error) {
      console.error('Invalid URL:', error);
      return null;
    }
  };

  // Load book details from URL
  const loadBookFromUrl = async (url) => {
    setLoadingBook(true);
    setBookError(null);

    try {
      const parsed = parseBookUrl(url);
      
      if (!parsed) {
        setBookError('Invalid URL format. Please enter a valid book URL.');
        setLoadingBook(false);
        return;
      }

      let bookId = null;
      let book = null;

      // Standard URL - direct book ID lookup
      if (parsed.type === 'standard') {
        bookId = parsed.bookId;
        const bookRef = doc(db, 'pb-books', bookId);
        const bookSnap = await getDoc(bookRef);

        if (!bookSnap.exists()) {
          setBookError('Book not found. Make sure the URL is correct and the book is published.');
          setLoadingBook(false);
          return;
        }

        book = bookSnap.data();
      } 
      // Custom domain - query by customDomain and customPath
      else if (parsed.type === 'custom') {
        const booksRef = collection(db, 'pb-books');
        
        // Query for books matching customDomain and customPath
        const q = query(
          booksRef,
          where('customDomain', '==', parsed.domain),
          where('customPath', '==', parsed.path)
        );
        
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setBookError(
            `No published book found at ${parsed.domain}${parsed.path ? '/' + parsed.path : ''}. ` +
            'Make sure the custom domain is correctly configured and the book is published.'
          );
          setLoadingBook(false);
          return;
        }

        // Should only be one match, but get the first one
        const bookDoc = snapshot.docs[0];
        bookId = bookDoc.id;
        book = bookDoc.data();
      }

      // Check if published
      if (!book.published) {
        setBookError('This book is not published. Only published books can be added as tools.');
        setLoadingBook(false);
        return;
      }

      // Verify it's actually accessible at the given URL
      // (the book might have customDomain set but not properly configured)
      const actualUrl = book.customDomain
        ? `https://${book.customDomain}${book.customPath ? '/' + book.customPath : ''}`
        : `${window.location.origin}/book/${bookId}`;

      // Auto-fill form with book details
      setFormData(prev => ({
        ...prev,
        sourceType: 'book',
        sourceBookId: bookId,
        name: prev.name || book.botName || book.title || 'Untitled Book',
        description: prev.description || book.description || `Published ${book.templateType || 'book'}`,
        url: actualUrl, // Use the actual URL from book settings
        embedCode: `<iframe src="${actualUrl}" width="100%" height="600" frameborder="0"></iframe>`,
        category: prev.category || book.templateType || 'book',
        tags: prev.tags.length > 0 ? prev.tags : [book.templateType || 'book', 'published'],
        icon: prev.icon || 'book',
        metadata: {
          templateType: book.templateType,
          customDomain: book.customDomain,
          customPath: book.customPath,
          published: book.published,
          addedAt: new Date().toISOString()
        }
      }));

      setLoadingBook(false);
    } catch (error) {
      console.error('Error loading book:', error);
      setBookError('Failed to load book details. Please check the URL and try again.');
      setLoadingBook(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.name) return;
    if (!isInternal && !formData.url) return;
    if (isInternal && contentMode === 'embed' && !formData.embedCode) return;
    if (isInternal && contentMode === 'html' && !formData.htmlCode) return;
    if (isInternal && contentMode === 'book-url' && !formData.url) return;

    onSave(formData);
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    if (formData.tags.includes(tagInput.trim())) return;
    setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {tool ? 'Edit Tool' : `Add ${isInternal ? 'Internal' : 'External'} Tool`}
            </h2>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* External Tool - URL */}
        {!isInternal && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">URL</label>
            <input
              type="url"
              value={formData.url}
              onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://example.com"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        {/* Internal Tool - Content Type Selection */}
        {isInternal && !tool && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
            <div className="grid grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => {
                  setContentMode('book-url');
                  setFormData(prev => ({ ...prev, embedCode: '', htmlCode: '' }));
                }}
                className={`p-4 border-2 rounded-lg transition-all ${
                  contentMode === 'book-url'
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Book className="w-6 h-6 mx-auto mb-2" />
                <div className="text-sm font-medium">Published Book</div>
                <div className="text-xs text-gray-500 mt-1">Add by URL</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setContentMode('embed');
                  setFormData(prev => ({ ...prev, htmlCode: '', sourceType: null, sourceBookId: null }));
                }}
                className={`p-4 border-2 rounded-lg transition-all ${
                  contentMode === 'embed'
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Code className="w-6 h-6 mx-auto mb-2" />
                <div className="text-sm font-medium">Embed Code</div>
                <div className="text-xs text-gray-500 mt-1">iframe, embed</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setContentMode('html');
                  setFormData(prev => ({ ...prev, embedCode: '', sourceType: null, sourceBookId: null }));
                }}
                className={`p-4 border-2 rounded-lg transition-all ${
                  contentMode === 'html'
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Globe className="w-6 h-6 mx-auto mb-2" />
                <div className="text-sm font-medium">HTML Widget</div>
                <div className="text-xs text-gray-500 mt-1">Custom code</div>
              </button>
            </div>
          </div>
        )}

        {/* Book URL Input */}
        {isInternal && contentMode === 'book-url' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Book URL</label>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="url"
                  value={formData.url}
                  onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://parrotpages.co.za/book/abc123 or https://mysite.com or https://mysite.com/path"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Button 
                  onClick={() => loadBookFromUrl(formData.url)}
                  disabled={!formData.url || loadingBook}
                  variant="secondary"
                >
                  {loadingBook ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Load Book
                    </>
                  )}
                </Button>
              </div>

              {bookError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{bookError}</p>
                </div>
              )}

              {formData.sourceBookId && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">Book loaded successfully</p>
                    <div className="text-xs text-green-700 mt-1 space-y-1">
                      {formData.metadata?.templateType && (
                        <div>Template: {formData.metadata.templateType.charAt(0).toUpperCase() + formData.metadata.templateType.slice(1)}</div>
                      )}
                      {formData.metadata?.customDomain && (
                        <div>
                          Domain: {formData.metadata.customDomain}
                          {formData.metadata?.customPath && ` / ${formData.metadata.customPath}`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800 font-medium mb-2">Supported URL formats:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Standard: <code className="bg-blue-100 px-1 rounded">https://parrotpages.co.za/book/abc123</code></li>
                  <li>• Custom domain (root): <code className="bg-blue-100 px-1 rounded">https://mystore.com</code></li>
                  <li>• Custom domain (with path): <code className="bg-blue-100 px-1 rounded">https://mysite.com/shop</code></li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Embed Code Input */}
        {isInternal && contentMode === 'embed' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Embed Code (iframe)</label>
            <textarea
              rows={4}
              value={formData.embedCode}
              onChange={e => setFormData(prev => ({ ...prev, embedCode: e.target.value }))}
              placeholder='<iframe src="https://example.com/embed" width="100%" height="400"></iframe>'
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
            />
          </div>
        )}

        {/* HTML Code Input */}
        {isInternal && contentMode === 'html' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">HTML Code</label>
            <textarea
              rows={6}
              value={formData.htmlCode}
              onChange={e => setFormData(prev => ({ ...prev, htmlCode: e.target.value }))}
              placeholder="<div>Custom HTML widget code...</div>"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
            />
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tool Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Google Analytics, Custom Calculator, My Photo Album"
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of what this tool does..."
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category (Optional)</label>
          <input
            type="text"
            value={formData.category}
            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
            placeholder="e.g., Analytics, Productivity, Social"
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add tag..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <Button onClick={addTag} variant="secondary">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {formData.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span key={tag} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  #{tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-indigo-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
          <Button onClick={onCancel} variant="secondary">
            <X className="w-4 h-4" /> Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.name || loadingBook}>
            <Check className="w-4 h-4" /> {tool ? 'Update' : 'Create'} Tool
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- TOOL VIEWER COMPONENT ---
const ToolViewer = ({ tool, onBack, onEdit, onDelete }) => {
  const isBookTool = tool.sourceType === 'book';
  const isEmbed = tool.embedCode && !tool.htmlCode;
  const isHtml = tool.htmlCode;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              {isBookTool && <Book className="w-6 h-6 text-purple-600" />}
              {isEmbed && <Code className="w-6 h-6 text-indigo-600" />}
              {isHtml && <Globe className="w-6 h-6 text-indigo-600" />}
              <h2 className="text-2xl font-bold text-gray-900">{tool.name}</h2>
            </div>
            {tool.description && (
              <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isBookTool && (
            <Button onClick={() => onEdit(tool)} variant="secondary" size="sm">
              <Edit2 className="w-4 h-4" /> Edit
            </Button>
          )}
          <Button onClick={() => onDelete(tool.id)} variant="secondary" size="sm">
            <Trash2 className="w-4 h-4" /> Delete
          </Button>
        </div>
      </div>

      {/* Tool Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">

        {/* Content Preview */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Preview</label>
          
          {/* Embed/Book Preview */}
          {(isEmbed || isBookTool) && tool.embedCode && (
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <div 
                dangerouslySetInnerHTML={{ __html: tool.embedCode }}
                className="w-full"
              />
            </div>
          )}

          {/* HTML Widget Preview */}
          {isHtml && tool.htmlCode && (
            <div className="space-y-3">
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-4">
                <div dangerouslySetInnerHTML={{ __html: tool.htmlCode }} />
              </div>
              <details className="bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 cursor-pointer font-medium text-sm text-gray-700 hover:bg-gray-100">
                  View HTML Code
                </summary>
                <div className="px-4 pb-4">
                  <pre className="text-xs bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto">
                    <code>{tool.htmlCode}</code>
                  </pre>
                </div>
              </details>
            </div>
          )}

          {/* External Link Info */}
          {!isEmbed && !isHtml && !isBookTool && tool.url && (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">This is an external link</p>
              <Button onClick={() => window.open(tool.url, '_blank')} variant="primary">
                <ExternalLink className="w-4 h-4" /> Open in New Tab
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      {isBookTool && tool.url && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Book className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">Published Book</div>
                <div className="text-xs text-gray-600">View-only reference to published content</div>
              </div>
            </div>
            <Button 
              onClick={() => window.open(tool.url, '_blank')}
              variant="secondary"
              size="sm"
            >
              <ExternalLink className="w-4 h-4" /> Open Book
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- BOOK SELECTOR MODAL ---
const BookSelectorModal = ({ isOpen, onClose, onSelect, bookId, tools = [] }) => {
  const [books, setBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [existingToolBooks, setExistingToolBooks] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      loadBooks();
    }
  }, [isOpen]);

  const loadBooks = async () => {
    try {
      setLoading(true);
      
      // Get user's published books
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const booksRef = collection(db, 'pb-books');
      const q = query(
        booksRef,
        where('ownerId', '==', currentUser.uid),
        where('published', '==', true),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const userBooks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get existing tools to filter out already added books
      const bookToolIds = new Set(
        tools
          .filter(t => t.sourceType === 'book' && t.sourceBookId)
          .map(t => t.sourceBookId)
      );

      setBooks(userBooks);
      setExistingToolBooks(bookToolIds);
    } catch (error) {
      console.error('Error loading books:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBook = (book) => {
    setSelectedBooks(prev => {
      const exists = prev.find(b => b.id === book.id);
      if (exists) {
        return prev.filter(b => b.id !== book.id);
      }
      return [...prev, book];
    });
  };

  const handleAdd = () => {
    onSelect(selectedBooks);
    onClose();
  };

  const availableBooks = books.filter(book => !existingToolBooks.has(book.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Published Books</h2>
            <p className="text-sm text-gray-600 mt-1">Select books to add as internal tools</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your published books...</p>
              </div>
            </div>
          ) : availableBooks.length === 0 ? (
            <div className="text-center py-12">
              <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No books available</h3>
              <p className="text-gray-600">
                {existingToolBooks.size > 0 
                  ? 'All your published books have already been added.'
                  : 'You don\'t have any published books yet.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableBooks.map(book => {
                const isSelected = selectedBooks.some(b => b.id === book.id);
                const templateLabel = book.templateType?.charAt(0).toUpperCase() + book.templateType?.slice(1) || 'Book';
                
                return (
                  <button
                    key={book.id}
                    onClick={() => toggleBook(book)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-indigo-100' : 'bg-gray-100'
                      }`}>
                        {isSelected ? (
                          <Check className="w-6 h-6 text-indigo-600" />
                        ) : (
                          <Book className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {book.botName || book.title || 'Untitled Book'}
                          </h4>
                          <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                            isSelected 
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {templateLabel}
                          </span>
                        </div>
                        
                        {book.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {book.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {book.customDomain && (
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {book.customDomain}
                            </span>
                          )}
                          {book.updatedAt && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(book.updatedAt.toDate?.() || book.updatedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedBooks.length > 0 && (
              <span>{selectedBooks.length} book{selectedBooks.length !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          <div className="flex gap-3">
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button 
              onClick={handleAdd} 
              disabled={selectedBooks.length === 0}
            >
              <Plus className="w-4 h-4" /> Add {selectedBooks.length > 0 ? `(${selectedBooks.length})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- DIGITAL TOOLS COMPONENTS ---
const ToolCard = ({ tool, onEdit, onDelete, onOpen }) => {
  const typeIcons = {
    [ToolType.EMBED]: Globe,
    [ToolType.LINK]: ExternalLink,
    [ToolType.HTML]: Code
  };
  const TypeIcon = typeIcons[tool.type] || Globe;

  return (
    <div 
      onClick={() => onOpen(tool)}
      className="bg-white rounded-xl border border-gray-100 p-5 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          tool.iconBg || 'bg-gradient-to-br from-indigo-500 to-purple-600'
        }`}>
          {tool.iconUrl ? (
            <img src={tool.iconUrl} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <TypeIcon className="w-6 h-6 text-white" />
          )}
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu trigger={
            <button className="p-2 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }>
            <MenuItem icon={Play} label="Open" onClick={() => onOpen(tool)} />
            <MenuItem icon={Edit2} label="Edit" onClick={() => onEdit(tool)} />
            <MenuItem icon={Trash2} label="Delete" onClick={() => onDelete(tool.id)} danger />
          </DropdownMenu>
        </div>
      </div>

      <h4 className="font-semibold text-gray-900 mb-1">{tool.name}</h4>
      <p className="text-sm text-gray-500 line-clamp-2">{tool.description || 'No description'}</p>

      <div className="flex items-center gap-2 mt-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          tool.type === ToolType.EMBED ? 'bg-purple-100 text-purple-700' :
          tool.type === ToolType.LINK ? 'bg-blue-100 text-blue-700' :
          'bg-orange-100 text-orange-700'
        }`}>
          {tool.type}
        </span>
        {tool.category && (
          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
            {tool.category}
          </span>
        )}
      </div>
    </div>
  );
};

const ToolViewModal = ({ isOpen, onClose, tool }) => {
  if (!tool) return null;

  if (tool.type === ToolType.LINK) {
    // For links, just open in new tab
    window.open(tool.url, '_blank');
    onClose();
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={tool.name} size="full">
      {tool.type === ToolType.EMBED && tool.url && (
        <div className="w-full h-[70vh] bg-gray-100 rounded-lg overflow-hidden">
          <iframe
            src={tool.url}
            title={tool.name}
            frameBorder="0"
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      )}

      {tool.type === ToolType.HTML && tool.htmlContent && (
        <div 
          className="w-full min-h-[50vh] bg-white rounded-lg p-4"
          dangerouslySetInnerHTML={{ __html: tool.htmlContent }}
        />
      )}
    </Modal>
  );
};

// --- MAIN COMPONENT ---
export const DiaryResourcesView = ({
  bookId,
  customStyling = {},
  media = [],
  assets = [],
  tools = [],
  onAddMedia,
  onUpdateMedia,
  onDeleteMedia,
  onAddAsset,
  onUpdateAsset,
  onDeleteAsset,
  onAddTool,
  onUpdateTool,
  onDeleteTool,
  onAddBooksAsTools,
  diaryService
}) => {
  // State
  const [activeTab, setActiveTab] = useState(ResourceTabs.MEDIA);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');

  // Media states
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [showMediaView, setShowMediaView] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [viewingMedia, setViewingMedia] = useState(null);

  // Asset states
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showAssetView, setShowAssetView] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [viewingAsset, setViewingAsset] = useState(null);

  // Tools states - Updated for navigation
  const [toolsView, setToolsView] = useState(ToolsViewState.LIST);
  const [editingTool, setEditingTool] = useState(null);
  const [viewingTool, setViewingTool] = useState(null);
  const [toolTypeFilter, setToolTypeFilter] = useState(null);
  const [showBookSelector, setShowBookSelector] = useState(false);

  // Filtered data
  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      if (searchQuery && !item.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCategory !== 'ALL' && item.type !== filterCategory) return false;
      return true;
    });
  }, [media, searchQuery, filterCategory]);

  const mediaCategories = useMemo(() => {
    const categories = media
      .map(m => m.category)
      .filter(Boolean);

    return Array.from(new Set(categories)).sort();
  }, [media]);

  const filteredAssets = useMemo(() => {
    return assets.filter(item => {
      if (searchQuery && !item.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCategory !== 'ALL' && item.category !== filterCategory) return false;
      return true;
    });
  }, [assets, searchQuery, filterCategory]);

  const filteredTools = useMemo(() => {
    return tools.filter(item => {
      if (searchQuery && !item.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterCategory !== 'ALL' && item.category !== filterCategory) return false;
      return true;
    });
  }, [tools, searchQuery, filterCategory]);

  // Calculate totals
  const totalAssetValue = useMemo(() => {
    return assets.reduce((sum, asset) => sum + (asset.value || 0), 0);
  }, [assets]);

  // Handlers
  const handleSaveMedia = (item) => {
    if (editingMedia) {
      onUpdateMedia?.(item);
    } else {
      onAddMedia?.(item);
    }
    setEditingMedia(null);
  };

  const handleSaveAsset = (item) => {
    if (editingAsset) {
      onUpdateAsset?.(item);
    } else {
      onAddAsset?.(item);
    }
    setEditingAsset(null);
  };

  const handleSaveTool = (tool) => {
    if (editingTool?.id) {
      onUpdateTool?.(tool);
    } else {
      onAddTool?.(tool);
    }
  };

  const handleDeleteToolLocal = (toolId) => {
    onDeleteTool?.(toolId);
  };

  const handleDeleteMedia = (id) => {
    if (window.confirm('Delete this media item?')) {
      onDeleteMedia?.(id);
    }
  };

  const handleDeleteAsset = (id) => {
    if (window.confirm('Delete this asset?')) {
      onDeleteAsset?.(id);
    }
  };

  const handleDeleteTool = (id) => {
    if (window.confirm('Delete this tool?')) {
      onDeleteTool?.(id);
    }
  };

  return (
    <div className="h-full flex-1 overflow-y-auto flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resources</h1>
            <p className="text-sm text-gray-500 mt-1">Manage documents, assets, and digital tools</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 -mb-4">
          <TabButton
            active={activeTab === ResourceTabs.MEDIA}
            onClick={() => { setActiveTab(ResourceTabs.MEDIA); setFilterCategory('ALL'); }}
            icon={FileText}
            label="Media"
            count={media.length}
            customStyling={customStyling}
          />
          <TabButton
            active={activeTab === ResourceTabs.ASSETS}
            onClick={() => { setActiveTab(ResourceTabs.ASSETS); setFilterCategory('ALL'); }}
            icon={Briefcase}
            label="Asset Register"
            count={assets.length}
            customStyling={customStyling}
          />
          <TabButton
            active={activeTab === ResourceTabs.TOOLS}
            onClick={() => { setActiveTab(ResourceTabs.TOOLS); setFilterCategory('ALL'); }}
            icon={Wrench}
            label="Digital Tools"
            count={tools.length}
            customStyling={customStyling}
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Category Filter */}
            {activeTab === ResourceTabs.MEDIA && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">All Types</option>
                {Object.values(MediaType).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            )}

            {activeTab === ResourceTabs.ASSETS && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="ALL">All Categories</option>
                {Object.entries(AssetCategoryConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            )}

            {/* View Toggle */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <Grid className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              >
                <List className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Add Button */}
          {activeTab === ResourceTabs.MEDIA && (
            <Button onClick={() => { setEditingMedia(null); setShowMediaForm(true); }}
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> Add Media
            </Button>
          )}
          {activeTab === ResourceTabs.ASSETS && (
            <Button onClick={() => { setEditingAsset(null); setShowAssetForm(true); }}
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> Add Asset
            </Button>
          )}
          {activeTab === ResourceTabs.TOOLS && toolsView === ToolsViewState.LIST && (
            <Button onClick={() => { setEditingTool(null); setToolsView(ToolsViewState.EDITOR); }}
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Plus className="w-4 h-4" /> Add Tool
            </Button>
          )}
        </div>

        {/* Asset Summary */}
        {activeTab === ResourceTabs.ASSETS && assets.length > 0 && (
          <div className="mt-3 flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-500">Total Assets:</span>
              <span className="ml-2 font-semibold">{assets.length}</span>
            </div>
            <div>
              <span className="text-gray-500">Total Value:</span>
              <span className="ml-2 font-semibold text-green-600">{formatCurrency(totalAssetValue)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
        {/* Media Tab */}
        {activeTab === ResourceTabs.MEDIA && (
          <>
            {filteredMedia.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No media yet"
                description="Store documents, videos, and other files here for easy access."
                action={
                  <Button onClick={() => { setEditingMedia(null); setShowMediaForm(true); }}>
                    <Plus className="w-4 h-4" /> Add Media
                  </Button>
                }
              />
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' 
                : 'space-y-3'
              }>
                {filteredMedia.map(item => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    onEdit={(item) => { setEditingMedia(item); setShowMediaForm(true); }}
                    onDelete={handleDeleteMedia}
                    onView={(item) => { setViewingMedia(item); setShowMediaView(true); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Assets Tab */}
        {activeTab === ResourceTabs.ASSETS && (
          <>
            {filteredAssets.length === 0 ? (
              <EmptyState
                icon={Briefcase}
                title="No assets registered"
                description="Keep track of your vehicles, properties, investments, and other valuables."
                action={
                  <Button onClick={() => { setEditingAsset(null); setShowAssetForm(true); }}>
                    <Plus className="w-4 h-4" /> Add Asset
                  </Button>
                }
              />
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
                : 'space-y-3'
              }>
                {filteredAssets.map(asset => (
                  <AssetCard
                    key={asset.id}
                    asset={asset}
                    onEdit={(asset) => { setEditingAsset(asset); setShowAssetForm(true); }}
                    onDelete={handleDeleteAsset}
                    onView={(asset) => { setViewingAsset(asset); setShowAssetView(true); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Tools Tab */}
        {/* Tools Tab */}
        {activeTab === ResourceTabs.TOOLS && (
          <>
            {/* Overview */}
            {toolsView === ToolsViewState.LIST && (
              <ToolsOverview
                tools={tools}
                onNavigate={(viewState) => {
                  setToolsView(viewState);
                  setToolTypeFilter(viewState === ToolsViewState.INTERNAL ? ToolType.INTERNAL : ToolType.EXTERNAL);
                }}
                onAdd={(type) => {
                  setEditingTool({ type });
                  setToolsView(ToolsViewState.EDITOR);
                }}
                onAddBooks={() => setShowBookSelector(true)}
              />
            )}

            {/* Internal/External List */}
            {(toolsView === ToolsViewState.INTERNAL || toolsView === ToolsViewState.EXTERNAL) && (
              <ToolsList
                tools={tools}
                type={toolTypeFilter}
                viewMode={viewMode}
                onEdit={(tool) => {
                  setEditingTool(tool);
                  setToolsView(ToolsViewState.EDITOR);
                }}
                onDelete={onDeleteTool}
                onOpen={(tool) => {
                  if (tool.type === ToolType.EXTERNAL) {
                    window.open(tool.url, '_blank');
                  } else {
                    setViewingTool(tool);
                    setToolsView(ToolsViewState.VIEWER);
                  }
                }}
                onBack={() => setToolsView(ToolsViewState.LIST)}
              />
            )}

            {/* Tool Viewer */}
            {toolsView === ToolsViewState.VIEWER && viewingTool && (
              <ToolViewer
                tool={viewingTool}
                onBack={() => {
                  setViewingTool(null);
                  setToolsView(toolTypeFilter ? (toolTypeFilter === ToolType.INTERNAL ? ToolsViewState.INTERNAL : ToolsViewState.EXTERNAL) : ToolsViewState.LIST);
                }}
                onEdit={(tool) => {
                  setEditingTool(tool);
                  setViewingTool(null);
                  setToolsView(ToolsViewState.EDITOR);
                }}
                onDelete={(toolId) => {
                  if (window.confirm('Delete this tool?')) {
                    onDeleteTool(toolId);
                    setViewingTool(null);
                    setToolsView(ToolsViewState.LIST);
                  }
                }}
              />
            )}

            {/* Tool Editor */}
            {toolsView === ToolsViewState.EDITOR && (
              <ToolEditor
                tool={editingTool?.id ? editingTool : null}
                type={editingTool?.type}
                onSave={(tool) => {
                  handleSaveTool(tool);
                  setToolsView(ToolsViewState.LIST);
                  setEditingTool(null);
                }}
                onCancel={() => {
                  setToolsView(ToolsViewState.LIST);
                  setEditingTool(null);
                }}
              />
            )}
          </>
        )}

      {/* Modals */}
      <MediaFormModal
        isOpen={showMediaForm}
        onClose={() => { setShowMediaForm(false); setEditingMedia(null); }}
        onSave={handleSaveMedia}
        item={editingMedia}
        mediaCategories={mediaCategories}
      />

      <MediaViewModal
        isOpen={showMediaView}
        onClose={() => { setShowMediaView(false); setViewingMedia(null); }}
        item={viewingMedia}
      />

      <AssetFormModal
        isOpen={showAssetForm}
        onClose={() => { setShowAssetForm(false); setEditingAsset(null); }}
        onSave={handleSaveAsset}
        asset={editingAsset}
      />

      <AssetViewModal
        isOpen={showAssetView}
        onClose={() => { setShowAssetView(false); setViewingAsset(null); }}
        asset={viewingAsset}
      />

      <BookSelectorModal
        isOpen={showBookSelector}
        onClose={() => setShowBookSelector(false)}
        onSelect={onAddBooksAsTools}
        bookId={bookId}
        tools={tools}
      />
    </div>
  );
};

export default DiaryResourcesView;